import { Controller } from "@hotwired/stimulus";

export default class FlashController extends Controller {

  connect() {
    this.handleBeforeCache = () => {
      this.disconnect();
      this.element.remove();
    };
    document.addEventListener("turbo:before-cache", this.handleBeforeCache);
    globalThis.addEventListener("pagehide", this.handleBeforeCache);
    this.translate();
  }

  async translate() {
    if (globalThis.performance?.getEntriesByType("navigation")[0]?.type === "back_forward") {
      this.element.remove();
      return;
    }

    this.active = true;
    // History navigation must not suppress fresh flashes from later actions.
    // Mark this element so restoring/reconnecting it cannot replay an old flash.
    if (this.element.dataset.flashShown === "true") return;

    const message = this.element.dataset.flashMessage;
    const type = this.element.dataset.flashType || "success";
    if (!message) return;
    this.element.dataset.flashShown = "true";

    const needsTranslation = (localStorage.getItem("selected_lang") || "en") !== "en";
    const skeleton = '<div class="flash-message-skeleton" aria-hidden="true"><span></span><span></span></div>';
    let dismissed = false;
    const alert = this.showAlert(needsTranslation ? skeleton : message, type).then(() => {
      dismissed = true;
      this.cancelTranslation?.();
      this.cancelTranslatorWait?.();
      if (this.active) this.element.remove();
    });
    const popup = Swal.getPopup();
    this.popup = popup;
    // SweetAlert renders outside the temporary flash element. Exclude its
    // generated container from Turbo snapshots as well.
    Swal.getContainer()?.setAttribute("data-turbo-temporary", "");

    if (needsTranslation) {
      Swal.stopTimer();
      Swal.getHtmlContainer()?.setAttribute("aria-busy", "true");
      const translatedMessage = await this.translateMessage(message);
      if (!this.active || dismissed || Swal.getPopup() !== popup) return;

      Swal.update({ html: translatedMessage });
      Swal.getHtmlContainer()?.removeAttribute("aria-busy");
      // Give the user the full reading time after translation finishes.
      Swal.resumeTimer();
    }
    await alert;
  }

  async translateMessage(message) {
    try {
      // Translate text nodes only, preserving markup and escaping translated text.
      const source = document.createElement("div");
      source.innerHTML = message;
      const walker = document.createTreeWalker(source, NodeFilter.SHOW_TEXT);
      const nodes = [];
      while (walker.nextNode()) {
        if (walker.currentNode.textContent.trim()) nodes.push(walker.currentNode);
      }
      if (!nodes.length) return message;

      const texts = nodes.map(node => node.textContent);
      const translations = await Promise.race([
        this.waitForTranslator().then(translator => translator?.translateInterfaceTexts(texts) || texts),
        new Promise(resolve => {
          // Allow widget initialization plus its 15-second translation window.
          this.translationFallback = globalThis.setTimeout(() => resolve(texts), 30000);
          this.cancelTranslation = () => resolve(texts);
        })
      ]);
      for (const [index, node] of nodes.entries()) {
        const translated = translations?.[index];
        if (typeof translated === "string" && translated.trim()) node.textContent = translated;
      }
      return source.innerHTML;
    } catch {
      return message;
    } finally {
      globalThis.clearTimeout(this.translationFallback);
      this.cancelTranslation = null;
      this.cancelTranslatorWait?.();
    }
  }

  waitForTranslator() {
    const element = document.getElementById("google-translate-block");
    const translator = element && this.application.getControllerForElementAndIdentifier(
      element, "google-translate"
    );
    if (this.translatorIsReady(translator)) return Promise.resolve(translator);
    if (!element) return Promise.resolve(null);

    // Controller connection precedes widget initialization and language selection.
    return new Promise(resolve => {
      const check = () => {
        const controller = this.application.getControllerForElementAndIdentifier(
          element, "google-translate"
        );
        if (!this.translatorIsReady(controller)) return;
        cleanup();
        resolve(controller);
      };
      const cleanup = () => {
        globalThis.clearInterval(interval);
        document.removeEventListener("google-translate:ready", check);
        this.cancelTranslatorWait = null;
      };
      const interval = globalThis.setInterval(check, 100);
      this.cancelTranslatorWait = () => {
        cleanup();
        resolve(null);
      };
      document.addEventListener("google-translate:ready", check);
      check();
    });
  }

  translatorIsReady(translator) {
    if (!translator?.translateInterfaceTexts) return false;
    const language = localStorage.getItem("selected_lang") || "en";
    return language === "en" || document.querySelector(".goog-te-combo")?.value === language;
  }

  showAlert(message, type) {
    return Swal.fire({
      customClass: {
        popup: "swal-popup alert-sm-bg align-items-center justify-content-center w-100",
        htmlContainer: "text-black-paint text-white notranslate"
      },
      animation: false,
      html: message,
      icon: type,
      showCloseButton: true,
      buttonsStyling: false,
      showConfirmButton: false,
      timer: 4000,
      allowEscapeKey: true,
      allowOutsideClick: true
    });
  }

  disconnect() {
    document.removeEventListener("turbo:before-cache", this.handleBeforeCache);
    globalThis.removeEventListener("pagehide", this.handleBeforeCache);
    this.active = false;
    if (this.popup && Swal.getPopup() === this.popup) Swal.close();
    this.cancelTranslation?.();
    this.cancelTranslatorWait?.();
    globalThis.clearTimeout(this.translationFallback);
  }
}
